import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, Mail } from "lucide-react";

interface SEOErrorStateProps {
  title?: string;
  message?: string;
  errorCode?: string;
  website?: string;
}

const SEOErrorState = ({
  title = "SEO Audit Error",
  message = "We encountered an issue while analyzing your website.",
  errorCode,
  website
}: SEOErrorStateProps) => {
  return (
    <div className="container max-w-4xl mx-auto py-8">
      <Card className="border-destructive/20">
        <CardHeader className="bg-destructive/5 border-b border-destructive/10">
          <CardTitle className="text-lg flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="p-6">
            <div className="flex flex-col items-center text-center space-y-6 py-4">
              <div className="relative w-24 h-24 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="h-12 w-12 text-destructive" />
                </div>
              </div>

              <div className="space-y-3 max-w-lg">
                <h3 className="text-xl font-semibold">We couldn't complete your SEO audit</h3>
                <p className="text-muted-foreground">{message}</p>
                {errorCode && (
                  <div className="inline-block bg-muted px-3 py-1 rounded text-xs text-muted-foreground">
                    Error code: {errorCode}
                  </div>
                )}
              </div>

              <div className="pt-2">
                <Button 
                  variant="default" 
                  className="flex items-center gap-2 bg-primary"
                  asChild
                >
                  <a 
                    href={`mailto:support@creator.li?subject=SEO Audit Error: ${errorCode || 'Unknown'}&body=Hello Support Team,%0A%0AI encountered an error while running an SEO audit with the following details:%0A%0AWebsite: ${website ? encodeURIComponent(website) : 'Not specified'}%0AError Message: ${encodeURIComponent(message)}%0AError Code: ${errorCode || 'Unknown'}%0A%0APlease help me resolve this issue.%0A%0AThank you.`}
                  >
                    <Mail className="h-4 w-4" />
                    Contact Support
                  </a>
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 p-4 border-t">
            <div className="text-sm text-muted-foreground">
              <p className="font-medium">Troubleshooting tips:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Check your website's accessibility</li>
                <li>Verify your site doesn't block our crawler</li>
                <li>Ensure your robots.txt allows crawling</li>
                <li>Try running the audit again in a few minutes</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOErrorState;
